/**
 * FeedRoute — `/familie` family feed (Fase 5).
 *
 * The dedicated feed surface (decision: own route linked from the dashboard,
 * not a 6th bottom-bar tab). Shows the household's celebratory moments newest
 * first — approved chores/homework, achievement unlocks, streak milestones,
 * and approved kid-proposals — each reactable with the fixed emoji set.
 *
 * useFeedWS() is mounted app-wide (App's RealtimeSync) so the list stays live;
 * this route just reads the ['feed'] query and fires the reaction toggle.
 */
import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { EmptyState } from '../../components/primitives';
import { FeedCard } from './FeedCard';
import { useFeed, useReactToFeed } from './useFeedQuery';

export function FeedRoute(): ReactNode {
  const { t } = useTranslation();
  const feed = useFeed();
  const react = useReactToFeed();

  const rows = useMemo(() => feed.data ?? [], [feed.data]);
  const count = rows.length;

  return (
    <main data-testid="feed-route" className="min-h-dvh p-8">
      <header className="mb-5 flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <p className="text-ink-2 text-xs font-semibold tracking-widest uppercase">
            {t('feed.title')}
          </p>
          <h1 className="font-display text-display-s text-ink mt-1">{t('feed.headerDisplay')}</h1>
        </div>
        <Link to="/" data-testid="feed-to-home" className="text-ink-2 hover:text-ink text-sm font-semibold">
          {t('feed.backToHome')}
        </Link>
      </header>

      {count === 0 ? (
        <section data-testid="feed-empty">
          <EmptyState
            variant="page"
            heading={t('feed.empty.heading')}
            body={t('feed.empty.body')}
          />
        </section>
      ) : (
        <div data-testid="feed-list" className="mx-auto flex w-full max-w-screen-sm flex-col gap-3">
          {rows.map((item) => (
            <FeedCard
              key={item.id}
              item={item}
              onReact={(emoji) => react.mutate({ itemId: item.id, emoji })}
            />
          ))}
        </div>
      )}
    </main>
  );
}
